// Dated portraits on a person page: the "through the years" strip swaps
// the main portrait, and the timeline shows the portrait nearest the date
// under the pointer (or finger), with its year and the person's age.

// "Through the years": picking a thumbnail shows it as the main portrait.
export function portraitYears(main) {
  if (!main) return;
  const img = main.querySelector("img.person_portrait");
  const credit = main.querySelector("[data-portrait-credit]");
  const buttons = main.querySelectorAll(".portrait_year");
  if (!img || buttons.length === 0) return;

  buttons.forEach((button) => {
    button.addEventListener("click", () => {
      img.src = button.dataset.src;
      img.style.display = "";
      if (credit) credit.innerHTML = button.dataset.credit;
      buttons.forEach((other) => other.setAttribute("aria-pressed", String(other === button)));
    });
  });
}

const DAY = 24 * 60 * 60 * 1000;

// The year gridlines are where 1 January of each labelled year falls, so
// a point's date is read off them, not from fixed pixel widths: rows
// shrink to fit the box on small screens.
function gridlines(timeline) {
  return Array.from(timeline.querySelectorAll(".x_axis_grid")).map((cell) => ({
    year: parseInt(cell.textContent, 10),
    x: cell.getBoundingClientRect().left,
  })).filter((line) => !Number.isNaN(line.year));
}

function dateAt(lines, x) {
  if (lines.length < 2) return null;
  let i = lines.findIndex((line) => line.x > x) - 1;
  if (i < 0) i = x < lines[0].x ? 0 : lines.length - 2;
  const a = lines[i];
  const b = lines[i + 1];
  const year = a.year + ((x - a.x) / (b.x - a.x)) * (b.year - a.year);
  const whole = Math.floor(year);
  const date = new Date(Date.UTC(2000, 0, 1) + (year - whole) * 365.2425 * DAY);
  date.setUTCFullYear(whole);
  return date;
}

// Days from the date to when a portrait shows the person; 0 within its
// span. Matches Person#portrait_on.
function distance(portrait, date) {
  if (date < portrait.start) return portrait.start - date;
  if (date > portrait.end) return date - portrait.end;
  return 0;
}

export function timelinePortraits(timeline) {
  if (!timeline) return;
  const portraits = JSON.parse(timeline.dataset.portraitYears).map((p) => ({
    ...p,
    start: new Date(`${p.start}T00:00:00Z`),
    end: new Date(`${p.end}T00:00:00Z`),
  }));

  const line = document.createElement("div");
  line.className = "timeline_cursor";
  line.hidden = true;
  const card = document.createElement("figure");
  card.className = "timeline_portrait";
  card.hidden = true;
  card.innerHTML = '<span class="portrait_wrap timeline_portrait_img"><img class="timeline_portrait_img" alt=""></span><figcaption class="timeline_portrait_label"></figcaption>';
  timeline.append(line, card);
  const cardImg = card.querySelector("img");
  const cardLabel = card.querySelector("figcaption");

  function show(clientX) {
    const date = dateAt(gridlines(timeline), clientX);
    if (!date) return;
    const nearest = portraits.reduce((best, p) => (distance(p, date) < distance(best, date) ? p : best));
    if (cardImg.getAttribute("src") !== nearest.src) cardImg.src = nearest.src;
    cardLabel.textContent = nearest.label;

    const box = timeline.getBoundingClientRect();
    const x = clientX - box.left + timeline.scrollLeft;
    line.style.left = `${x}px`;
    // Keep the card inside the box: it flips to the left of the line near
    // the right edge.
    const width = card.offsetWidth || 96;
    const flip = clientX - box.left + width + 12 > timeline.clientWidth;
    card.style.left = `${flip ? x - width - 8 : x + 8}px`;
    line.hidden = false;
    card.hidden = false;
  }

  function hide() {
    line.hidden = true;
    card.hidden = true;
  }

  // Mouse: follow the pointer. Touch and pen: a tap shows the card there,
  // and dragging along the timeline moves it, until a tap elsewhere.
  timeline.addEventListener("pointermove", (event) => {
    if (event.pointerType === "mouse" || !card.hidden) show(event.clientX);
  });
  timeline.addEventListener("pointerdown", (event) => {
    if (event.pointerType !== "mouse") show(event.clientX);
  });
  timeline.addEventListener("pointerleave", (event) => {
    if (event.pointerType === "mouse") hide();
  });
  document.addEventListener("pointerdown", (event) => {
    if (!timeline.contains(event.target)) hide();
  });
}
